/**
 * Product page tracking. Same file on every Noritamy theme (IL, US) — keep it identical.
 *
 * Pushes to `window.dataLayer` only; GTM forwards to GA4 and Meta (docs/product-page-tracking.md).
 * It reads the page and listens to events the theme already dispatches, so no product template,
 * block or other script depends on it.
 *
 * Interaction events use the names and fields of the landing page (docs/landing-tracking.md),
 * with `tile_window: 'pdp'`:
 *   tile_image_swipe     another gallery photo is shown
 *   tile_image_zoom      the full-screen viewer opens
 *   tile_variant_select  an option is picked (metal, size…)
 *   tile_letter_select   a letter option is picked
 *   tile_details_open    an accordion row is opened
 *
 * `product_deep_view` is the campaign optimization event (Meta: DeepProductView14K), for products
 * whose add to cart is too rare to learn from. Once per browser session and `pdp_mode`, so a standard
 * product seen first never uses up the studio event, when the shopper has seen the price and then
 * shown interest, reported as `deep_trigger`:
 *   second_product   the price of a second product of the same pdp_mode was seen this session
 *   image_swipe      DEEP_SWIPES more photos seen
 *   variant_select   an option or letter was picked
 *   contact          WhatsApp or directions tapped
 * Interest shown before the price is seen (photo swipes on a phone) is held until it is.
 * Time on page and scroll depth are left out on purpose: they reward an open tab.
 */
(function () {
  'use strict';
  if (window.NoritamyProductTracking) return;

  var VERSION = 'v2_tile_tracking';
  var WINDOW_NAME = 'pdp';
  var DEEP_FIRED_KEY = 'nr_deep_view';
  var DEEP_SEEN_KEY = 'nr_deep_seen';
  var DEEP_SWIPES = 2;
  var LETTER_OPTION = /letter|initial|אות/i;
  var MATERIAL_OPTION = /finish|material|metal|colou?r|גימור|חומר|צבע/i;

  // Tag Manager keeps the last value of every data layer key, so each event states all of the
  // shared fields, null when they do not apply, and none leaks into the next event.
  var EVENT_FIELDS = {
    position_served: null,
    position_shown: null,
    personalized: null,
    tile_product_id: null,
    tile_price: null,
    tile_discount_pct: null,
    tile_on_sale: null,
    tile_material: null,
    tile_letter: null,
    tile_window: null,
    tile_click_source: null,
    tile_collection: null,
    media_type: null,
    details_name: null,
    image_index: null,
    option_name: null,
    option_value: null,
    pdp_mode: null,
    deep_trigger: null,
  };

  function safe(fn, fallback) {
    try { return fn(); } catch (e) { return fallback; }
  }

  var meta = safe(function () { return window.ShopifyAnalytics.meta; }, null) || {};
  var product = meta.product;
  var gallery = document.querySelector('media-gallery');
  var root = gallery && gallery.closest('.product-information');
  if (!product || !root) return;

  var pdpMode = root.querySelector('.studio-cta--card') ? 'studio' : 'standard';

  /* ------------------------------------------------------------ product */

  function picker() {
    return root.querySelector('variant-picker');
  }

  /** [{ name, value }] for every option the shopper has picked; placeholders are skipped. */
  function selection() {
    var el = picker();
    var out = [];
    if (!el) return out;
    el.querySelectorAll('select').forEach(function (select) {
      var option = select.options[select.selectedIndex];
      if (!select.value || !option || option.dataset.optionValueId === 'unselected') return;
      out.push({ name: optionName(select), value: option.value });
    });
    el.querySelectorAll('fieldset').forEach(function (fieldset) {
      var checked = fieldset.querySelector('input:checked');
      if (checked) out.push({ name: optionName(fieldset), value: checked.value });
    });
    return out;
  }

  function optionName(el) {
    var label = el.tagName === 'FIELDSET'
      ? el.querySelector('legend')
      : (el.id && root.querySelector('label[for="' + el.id + '"]')) || el.closest('.variant-option');
    var text = label ? label.textContent : el.getAttribute('name') || '';
    return text.split(':')[0].split('\n').map(function (s) { return s.trim(); }).filter(Boolean)[0] || null;
  }

  // The variant of the shopper's last pick, from the picked option itself: the add to cart form
  // follows only after variant-picker's section fetch, and ?variant= is written on the main product
  // template only.
  var pickedVariantId = null;

  function currentVariant() {
    // Not the first input[name="id"]: the Shop Pay installments form holds the initial variant forever.
    var input = root.querySelector('product-form-component input[name="id"]');
    var id = pickedVariantId || new URLSearchParams(window.location.search).get('variant') || (input && input.value) || meta.selectedVariantId;
    var variants = product.variants || [];
    return variants.find(function (v) { return String(v.id) === String(id); }) || variants[0] || null;
  }

  /** Shopify names a variant "<product title> - <public_title>"; titles may contain " - " too. */
  function itemName(variant) {
    if (!variant || !variant.name) return document.title;
    var suffix = variant.public_title ? ' - ' + variant.public_title : '';
    return suffix && variant.name.slice(-suffix.length) === suffix ? variant.name.slice(0, -suffix.length) : variant.name;
  }

  function push(event, fields) {
    var variant = currentVariant();
    var price = variant && typeof variant.price === 'number' ? Math.round(variant.price) / 100 : null;
    var currency = meta.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || null;
    var material = selection().find(function (o) { return o.name && MATERIAL_OPTION.test(o.name); });
    var item = {
      item_id: String(product.id),
      item_variant: variant ? String(variant.id) : null,
      item_name: itemName(variant),
      item_handle: window.location.pathname.split('/products/')[1] ? window.location.pathname.split('/products/')[1].split('/')[0] : null,
      price: price,
      quantity: 1,
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    var payload = {};
    Object.keys(EVENT_FIELDS).forEach(function (k) { payload[k] = EVENT_FIELDS[k]; });
    payload.event = event;
    payload.feature_version = VERSION;
    payload.tile_window = WINDOW_NAME;
    payload.tile_product_id = item.item_id;
    payload.tile_price = price;
    payload.tile_material = material ? material.value : null;
    payload.pdp_mode = pdpMode;
    Object.keys(fields || {}).forEach(function (k) { payload[k] = fields[k]; });
    payload.ecommerce = { currency: currency, value: price, items: [item] };
    window.dataLayer.push(payload);
  }

  /* ---------------------------------------------------------- deep view */

  var store = {
    get: function (key) { return safe(function () { return sessionStorage.getItem(key); }, null); },
    set: function (key, value) { safe(function () { sessionStorage.setItem(key, value); }); },
  };
  var deepDone = store.get(DEEP_FIRED_KEY + '_' + pdpMode) === '1';
  var priceSeen = false;
  var pendingTrigger = null;
  var swiped = {};

  function fireDeep(trigger) {
    if (deepDone) return;
    deepDone = true;
    store.set(DEEP_FIRED_KEY + '_' + pdpMode, '1');
    push('product_deep_view', { deep_trigger: trigger });
  }

  function interest(trigger) {
    if (deepDone) return;
    if (priceSeen) fireDeep(trigger);
    else pendingTrigger = pendingTrigger || trigger;
  }

  function onPriceSeen() {
    if (priceSeen) return;
    priceSeen = true;
    if (deepDone) return;
    var ids = (store.get(DEEP_SEEN_KEY + '_' + pdpMode) || '').split(',').filter(Boolean);
    if (ids.indexOf(String(product.id)) === -1) ids.push(String(product.id));
    store.set(DEEP_SEEN_KEY + '_' + pdpMode, ids.join(','));
    if (ids.length > 1) fireDeep('second_product');
    else if (pendingTrigger) fireDeep(pendingTrigger);
  }

  function watchPrice() {
    var target = root.querySelector('.studio-cta--card') || root.querySelector('product-price');
    if (!target || !('IntersectionObserver' in window)) return;
    var observer = new IntersectionObserver(function (entries) {
      if (entries.some(function (e) { return e.isIntersecting; })) {
        observer.disconnect();
        onPriceSeen();
      }
    }, { threshold: 0.5 });
    observer.observe(target);
  }

  /* ------------------------------------------------------------ sources */

  function mediaType(slide) {
    return slide && slide.querySelector('video, deferred-media, model-viewer, iframe') ? 'video' : 'photo';
  }

  var shownIndex = 0;
  root.addEventListener('slideshow:select', function (e) {
    var detail = e.detail;
    if (!detail || !(e.target instanceof Element) || !e.target.closest('media-gallery')) return;
    if (e.target.closest('zoom-dialog')) return;
    shownIndex = detail.index;
    if (!detail.userInitiated || detail.index === detail.previousIndex) return;
    push('tile_image_swipe', { image_index: detail.index + 1, media_type: mediaType(detail.slide) });
    swiped[detail.index] = true;
    if (Object.keys(swiped).length >= DEEP_SWIPES) interest('image_swipe');
  });

  // The full-screen viewer is a <dialog>; its `open` attribute is the only signal for every way in.
  // Watched on root: media-gallery replaces itself, zoom dialog included, on every variant update.
  new MutationObserver(function (records) {
    records.forEach(function (record) {
      var dialog = record.target;
      if (dialog.open && dialog.matches('zoom-dialog dialog')) push('tile_image_zoom', { image_index: shownIndex + 1 });
    });
  }).observe(root, { subtree: true, attributes: true, attributeFilter: ['open'] });

  root.addEventListener('change', function (e) {
    var field = e.target;
    if (!e.isTrusted || !(field instanceof Element) || !field.closest('variant-picker')) return;
    var group = field.closest('fieldset') || field;
    var name = optionName(group);
    var value = field.value || null;
    var picked = field.tagName === 'SELECT' ? field.options[field.selectedIndex] : field;
    if (picked && picked.dataset.variantId) pickedVariantId = picked.dataset.variantId;
    if (name && LETTER_OPTION.test(name)) push('tile_letter_select', { tile_letter: value, option_name: name, option_value: value });
    else push('tile_variant_select', { option_name: name, option_value: value });
    interest('variant_select');
  });

  root.addEventListener('click', function (e) {
    var summary = e.target instanceof Element && e.target.closest('accordion-custom summary');
    if (!summary) return;
    var details = summary.closest('details');
    // `open` flips after the click is handled.
    setTimeout(function () {
      if (details && details.open) push('tile_details_open', { details_name: summary.textContent.trim().replace(/\s+/g, ' ').slice(0, 60) });
    }, 0);
  });

  document.addEventListener('studio:action', function (e) {
    var action = e.detail && e.detail.action;
    if (action === 'whatsapp' || action === 'directions') interest('contact');
  });
  document.addEventListener('click', function (e) {
    var link = e.target instanceof Element && e.target.closest('.floating__btn, a[href*="wa.me"], a[href*="api.whatsapp.com"]');
    if (link && !link.closest('[data-studio-action]')) interest('contact');
  }, true);

  watchPrice();
  window.NoritamyProductTracking = { version: VERSION, pdpMode: pdpMode };
})();
